<template>
  <v-container fluid class="ncrm-workflows">
    <NcrmHomePageHeader
      icon="mdi-sitemap-outline"
      :title="$t('ncrm.workflows.title')"
      :subtitle="$t('ncrm.workflows.subtitle')"
      data-tour="workflows-header"
    >
      <v-btn color="secondary" variant="tonal" @click="openExternalApiSettings">
        <v-icon start>mdi-open-in-new</v-icon>
        {{ $t("ncrm.workflows.actions.openExternalApi") }}
      </v-btn>
      <v-btn color="primary" data-tour="workflows-create" @click="openCreateWorkflow">
        <v-icon start>mdi-plus</v-icon>
        {{ $t("ncrm.workflows.actions.new") }}
      </v-btn>
    </NcrmHomePageHeader>

    <v-alert v-if="errorMessage" type="error" class="mt-4">
      {{ errorMessage }}
    </v-alert>

    <NcrmHomeKpiCards class="mt-4" :items="kpiItems" data-tour="workflows-kpi" />

    <v-card class="mt-4 ncrm-workflows__panel" data-tour="workflows-list">
      <div class="ncrm-workflows__panel-header">
        <div>
          <div class="text-subtitle-1 font-weight-semibold ncrm-text-strong">
            {{ $t("ncrm.workflows.table.title") }}
          </div>
          <div class="text-body-2 text-medium-emphasis">
            {{ $t("ncrm.workflows.table.subtitle") }}
          </div>
        </div>
      </div>

      <v-data-table
        :headers="headers"
        :items="workflowRows"
        :loading="loadingWorkflows"
        item-key="id"
        density="comfortable"
        class="ncrm-workflows__table"
        :items-per-page="-1"
        hide-default-footer
        :row-props="workflowRowProps"
        @click:row="handleWorkflowClick"
      >
        <template #item.name="{ item }">
          <div class="ncrm-workflows__name-cell">
            <v-icon color="primary" size="18">mdi-transit-connection-variant</v-icon>
            <div class="min-w-0">
              <div class="ncrm-workflows__workflow-name">{{ item.name }}</div>
              <div class="ncrm-workflows__workflow-description">{{ item.description }}</div>
            </div>
          </div>
        </template>

        <template #item.status="{ item }">
          <v-chip color="info">
            {{ item.status }}
          </v-chip>
        </template>

        <template #item.sources="{ item }">
          <div class="ncrm-workflows__source-chips">
            <v-chip
              v-for="source in item.sourceList"
              :key="source"
              size="x-small"
              color="neutral"
            >
              {{ source }}
            </v-chip>
          </div>
        </template>

        <template #item.action="{ item }">
          <div class="d-flex align-center justify-end ga-1">
            <v-btn
              icon="mdi-pencil-outline"
              variant="text"
              size="small"
              :aria-label="$t('ncrm.workflows.actions.editWorkflow')"
              @click.stop="openEditWorkflow(item.raw)"
            />
            <v-icon class="ncrm-workflows__row-chevron" size="18">mdi-chevron-right</v-icon>
          </div>
        </template>

        <template #no-data>
          <v-alert type="info" class="ma-4">
            {{ $t("ncrm.workflows.empty") }}
          </v-alert>
        </template>
      </v-data-table>
    </v-card>

    <NcrmWorkflowEditorDrawer
      v-model="workflowDrawer"
      :mode="workflowDrawerMode"
      :workflow="editingWorkflow"
      :templates="workflowTemplates"
      @save="handleSaveWorkflow"
      @delete="handleDeleteWorkflow"
    />
  </v-container>
</template>

<script>
import axios from "axios";
import "./NcrmWorkflowsHome.styles.css";

import NcrmHomeKpiCards from "@/components/ncrm/shared/NcrmHomeKpiCards.vue";
import NcrmHomePageHeader from "@/components/ncrm/shared/NcrmHomePageHeader.vue";
import NcrmWorkflowEditorDrawer from "@/components/ncrm/workflows/NcrmWorkflowEditorDrawer.vue";
import { formatExternalApiDateTime } from "@/Utile/ncrmExternalApi.mjs";
import {
  buildWorkflowSummary,
  createWorkflowTemplates,
  formatWorkflowStatus,
  normalizeWorkflowInstance,
} from "@/Utile/ncrmWorkflows.mjs";
import {
  createWorkflowInstanceApi,
  deleteWorkflowInstanceApi,
  fetchWorkflowInstances,
  updateWorkflowInstanceApi,
} from "@/Utile/ncrmWorkflowApi.mjs";

export default {
  name: "NcrmWorkflowsHome",
  components: {
    NcrmHomeKpiCards,
    NcrmHomePageHeader,
    NcrmWorkflowEditorDrawer,
  },
  data() {
    return {
      workflows: [],
      workflowDrawer: false,
      workflowDrawerMode: "create",
      editingWorkflow: null,
      loadingExternalApi: false,
      loadingWorkflows: false,
      savingWorkflow: false,
      errorMessage: "",
      externalApiEndpoints: [],
    };
  },
  computed: {
    workflowTemplates() {
      return createWorkflowTemplates(this.$t);
    },
    kpiItems() {
      const activeCount = this.workflows.filter((workflow) => workflow.status === "active").length;
      return [
        {
          key: "total",
          label: this.$t("ncrm.workflows.kpis.total"),
          value: this.workflows.length,
          icon: "mdi-sitemap-outline",
          color: "primary",
        },
        {
          key: "active",
          label: this.$t("ncrm.workflows.kpis.active"),
          value: activeCount,
          icon: "mdi-play-circle-outline",
          color: "success",
        },
        {
          key: "attention",
          label: this.$t("ncrm.workflows.kpis.attention"),
          value: this.externalApiEndpoints.filter((endpoint) => endpoint.failed_count_24h).length,
          icon: "mdi-alert-outline",
          color: "warning",
        },
        {
          key: "manual",
          label: this.$t("ncrm.workflows.kpis.manual"),
          value: 0,
          icon: "mdi-account-edit-outline",
          color: "info",
        },
      ];
    },
    headers() {
      return [
        { title: this.$t("ncrm.workflows.table.headers.name"), key: "name", sortable: false },
        { title: this.$t("ncrm.workflows.table.headers.trigger"), key: "trigger", sortable: false },
        { title: this.$t("ncrm.workflows.table.headers.status"), key: "status", sortable: false },
        { title: this.$t("ncrm.workflows.table.headers.sources"), key: "sources", sortable: false },
        { title: this.$t("ncrm.workflows.table.headers.lastRun"), key: "lastRun", sortable: false },
        { title: this.$t("ncrm.workflows.table.headers.owner"), key: "owner", sortable: false },
        { title: this.$t("ncrm.workflows.table.headers.version"), key: "version", sortable: false },
        { title: this.$t("ncrm.workflows.table.headers.action"), key: "action", sortable: false, width: 88 },
      ];
    },
    workflowRows() {
      const templates = createWorkflowTemplates(this.$t);
      return this.workflows.map((workflow) => {
        const summary = buildWorkflowSummary(workflow, this.externalApiEndpoints);
        const template = templates.find((item) => item.key === workflow.templateKey);
        return {
          ...workflow,
          raw: workflow,
          description: template?.description || workflow.templateKey,
          trigger: summary.trigger,
          status: formatWorkflowStatus(workflow.status),
          sourceList: summary.sources,
          lastRun: summary.endpoint?.last_delivery_at
            ? formatExternalApiDateTime(summary.endpoint.last_delivery_at)
            : this.$t("ncrm.workflows.workflow.lastRun"),
          owner: this.$t("ncrm.workflows.workflow.owner"),
          version: workflow.version || "-",
        };
      });
    },
  },
  async mounted() {
    await this.fetchExternalApiWorkspace();
    await this.loadWorkflows();
  },
  methods: {
    templateName(templateKey) {
      return this.workflowTemplates.find((template) => template.key === templateKey)?.name || templateKey;
    },
    async loadWorkflows() {
      this.loadingWorkflows = true;
      try {
        const { data } = await fetchWorkflowInstances();
        const endpointId = this.externalApiEndpoints[0]?.id || null;
        this.workflows = (Array.isArray(data?.workflows) ? data.workflows : []).map((workflow) =>
          normalizeWorkflowInstance(workflow, {
            fallbackName: this.$t("ncrm.workflows.templates.enrichment.defaultInstanceName"),
            endpointId,
          })
        );
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || this.$t("ncrm.workflows.errors.workflowsLoad");
      } finally {
        this.loadingWorkflows = false;
      }
    },
    persistWorkflowLocally(workflow) {
      this.workflows = this.workflows.map((item) => (item.id === workflow.id ? workflow : item));
    },
    async fetchExternalApiWorkspace() {
      this.loadingExternalApi = true;
      this.errorMessage = "";
      try {
        const { data } = await axios.get("/ncrm/integrations/external-api/workspace");
        this.externalApiEndpoints = Array.isArray(data?.endpoints) ? data.endpoints : [];
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.error || this.$t("ncrm.workflows.errors.externalApiLoad");
      } finally {
        this.loadingExternalApi = false;
      }
    },
    workflowRowProps() {
      return {
        class: "ncrm-workflows__row",
        style: "cursor: pointer;",
        tabindex: 0,
      };
    },
    handleWorkflowClick(_, row) {
      const workflow = row?.item?.raw || row?.item || null;
      if (!workflow?.id) return;
      this.$router.push({ name: "NcrmWorkflowDetails", params: { workflowId: workflow.id } });
    },
    openCreateWorkflow() {
      this.workflowDrawerMode = "create";
      this.editingWorkflow = null;
      this.workflowDrawer = true;
    },
    openEditWorkflow(workflow) {
      this.workflowDrawerMode = "edit";
      this.editingWorkflow = workflow;
      this.workflowDrawer = true;
    },
    async handleSaveWorkflow(payload) {
      this.savingWorkflow = true;
      try {
        if (this.workflowDrawerMode === "edit" && this.editingWorkflow?.id) {
          const { data } = await updateWorkflowInstanceApi(this.editingWorkflow.id, payload);
          if (data?.workflow) this.persistWorkflowLocally(normalizeWorkflowInstance(data.workflow));
          this.workflowDrawer = false;
          return;
        }

        const { data } = await createWorkflowInstanceApi({
          ...payload,
          endpointId: this.externalApiEndpoints[0]?.id || null,
        });
        const workflow = normalizeWorkflowInstance(data?.workflow || {});
        this.workflows = [...this.workflows, workflow];
        this.workflowDrawer = false;
        this.$router.push({ name: "NcrmWorkflowDetails", params: { workflowId: workflow.id } });
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || this.$t("ncrm.workflows.errors.workflowSave");
      } finally {
        this.savingWorkflow = false;
      }
    },
    async handleDeleteWorkflow(workflow) {
      if (!workflow?.id) return;
      this.savingWorkflow = true;
      try {
        await deleteWorkflowInstanceApi(workflow.id);
        this.workflows = this.workflows.filter((item) => item.id !== workflow.id);
        this.workflowDrawer = false;
        this.editingWorkflow = null;
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || this.$t("ncrm.workflows.errors.workflowDelete");
      } finally {
        this.savingWorkflow = false;
      }
    },
    openExternalApiSettings() {
      this.$router.push({ path: "/ncrm/data-integrations", query: { tab: "external" } });
    },
  },
};
</script>
